import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  getUserStatistics,
  getRevenueStatistics,
  getUserChartData,
  getRevenueChartData,
} from '../../shared/services/analyticsApi';

// 간단 캐시/중복요청 방지 (TTL 30s)
const TTL = 30 * 1000;
const now = () => Date.now();
const userStatsCache = new Map(); // key -> { data, expiry }
const revenueStatsCache = new Map();
const inflightUserStats = new Map(); // key -> Promise
const inflightRevenueStats = new Map();

// 단일 일자 기반 통계 (위젯용)
export const useAnalyticsUserStats = (date, country = 'ALL') => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);
        const d = date || dayjs();
        const key = `${d.format('YYYY-MM-DD')}|${country}`;

        // 캐시 히트
        const cached = userStatsCache.get(key);
        if (cached && cached.expiry > now()) {
          setData(cached.data || {});
          setLoading(false);
          return;
        }

        // 이미 진행 중인 요청 사용
        if (inflightUserStats.has(key)) {
          const res = await inflightUserStats.get(key);
          setData(res || {});
          setLoading(false);
          return;
        }

        const dStr = d.format('YYYY-MM-DD');
        const params = { begin: dStr, end: dStr, country };
        const p = getUserStatistics(params).then((res) => {
          userStatsCache.set(key, { data: res || {}, expiry: now() + TTL });
          return res;
        }).finally(() => {
          inflightUserStats.delete(key);
        });
        inflightUserStats.set(key, p);
        const res = await p;
        setData(res || {});
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [date, country]);

  return { data, loading, error };
};

export const useAnalyticsRevenueStats = (date, country = 'ALL') => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);
        const d = date || dayjs();
        const key = `${d.format('YYYY-MM-DD')}|${country}`;

        const cached = revenueStatsCache.get(key);
        if (cached && cached.expiry > now()) {
          setData(cached.data || {});
          setLoading(false);
          return;
        }

        if (inflightRevenueStats.has(key)) {
          const res = await inflightRevenueStats.get(key);
          setData(res || {});
          setLoading(false);
          return;
        }

        const dStr = d.format('YYYY-MM-DD');
        const params = { begin: dStr, end: dStr, country };
        const p = getRevenueStatistics(params).then((res) => {
          revenueStatsCache.set(key, { data: res || {}, expiry: now() + TTL });
          return res;
        }).finally(() => {
          inflightRevenueStats.delete(key);
        });
        inflightRevenueStats.set(key, p);
        const res = await p;
        setData(res || {});
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [date, country]);

  return { data, loading, error };
};

// 기간 기반 차트 (유저 지표 공통)
export const useAnalyticsChart = (type, range, country = 'ALL') => {
  const [rows, setRows] = useState([]);
  const [periodTotal, setPeriodTotal] = useState(0);
  const [periodAverage, setPeriodAverage] = useState(0);
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchChart = async () => {
      try {
        setLoading(true);
        setError(null);
        const begin = (range?.[0] || dayjs()).format('YYYY-MM-DD');
        const end = (range?.[1] || dayjs()).format('YYYY-MM-DD');
        const res = await getUserChartData({ type, begin, end, country });
        setRows(res?.rows || []);
        setPeriodTotal(res?.periodTotal || 0);
        setPeriodAverage(res?.periodAverage || 0);
        setMonthlyStats(res?.monthlyStats || []);
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    };
    if (type) fetchChart();
  }, [type, range, country]);

  return { rows, periodTotal, periodAverage, monthlyStats, loading, error };
};

export const useAnalyticsRevenueChart = (range, country = 'ALL') => {
  const [rows, setRows] = useState([]);
  const [periodTotal, setPeriodTotal] = useState(0);
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchChart = async () => {
      try {
        setLoading(true);
        setError(null);
        const begin = (range?.[0] || dayjs()).format('YYYY-MM-DD');
        const end = (range?.[1] || dayjs()).format('YYYY-MM-DD');
        const res = await getRevenueChartData({ begin, end, type: 'revenue', country });
        setRows(res?.rows || []);
        setPeriodTotal(res?.periodTotal || 0);
        setMonthlyStats(res?.monthlyStats || []);
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    };
    fetchChart();
  }, [range, country]);

  return { rows, periodTotal, monthlyStats, loading, error };
};
